import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Loader from "../../../components/loader";
import api from "../../../services/api";
import toast from "react-hot-toast";
import FileInput from "../../../components/FileInput";
import SelectUser from "../../../components/selectUser";
import Select from "../../../components/Select";
import MultiSelect from "../../../components/MultiSelect";
import Comments from "../../../components/Comments";
import { ORG_IDS } from "@/config/organisations";

export default ({ project, setProject }) => {
  if (!project) return <Loader />;

  const user = useSelector((state) => state.Auth.user);

  const [budgets, setBudgets] = useState([]);
  const [organisations, setOrganisations] = useState([{ _id: "", name: "Select an organisation" }]);
  const [businessUnits, setBusinessUnits] = useState([]);

  useEffect(() => {
    getBudgets();
    fetchOrganisations();
    fetchBusinessUnits();
  }, []);

  const fetchOrganisations = async () => {
    try {
      const { data, ok } = await api.post("/organisation/search", { slug_only: true });
      if (ok && data) {
        setOrganisations([{ _id: "", name: "Select an organisation" }, ...data]);
      }
    } catch (error) {
      console.error("Error fetching organisations:", error);
    }
  };

  const fetchBusinessUnits = async () => {
    try {
      const { data, ok } = await api.get("/business_unit");
      if (ok && data) setBusinessUnits(data);
    } catch (error) {
      console.error("Error fetching business units:", error);
    }
  };

  const getBudgets = async () => {
    const { data } = await api.post("/budget/search", { projectId: project._id, status: "active" });
    setBudgets(data);
  };

  async function calculateTax(country, organisation_id) {
    let taxType = "";
    if (country && country === "France" && organisation_id === ORG_IDS.LE_COLLECTIF) {
      taxType = "FR 20%";
    } else if (country && country === "Netherlands" && organisation_id === ORG_IDS.SELEGO) {
      taxType = "NL 21%";
    } else if (organisation_id && country) {
      taxType = "N/A (Not applicable)";
    }
    return taxType;
  }

  async function onSave() {
    try {
      const { data, ok } = await api.put(`/project/${project._id}`, project);
      if (!ok) throw new Error("Error while updating project");
      setProject(data);
      toast.success(`${project.name} updated`);
    } catch (e) {
      console.error(e);
      toast.error("Some Error!");
    }
  }

  const handleAddComment = async (text) => {
    const comment = { text, user_id: user._id, user_name: user.name, user_avatar: user.avatar, created_at: new Date() };
    try {
      const { data, ok } = await api.put(`/project/${project._id}`, { comments: [...(project.comments || []), comment] });
      if (!ok) return toast.error("Failed to add comment");
      setProject(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to add comment");
    }
  };

  const handleDeleteComment = async (comment_id) => {
    try {
      const { data, ok } = await api.put(`/project/${project._id}`, { comments: (project.comments || []).filter((comment) => comment._id !== comment_id) });
      if (!ok) return toast.error("Failed to delete comment");
      setProject(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete comment");
    }
  };

  return (
    <div>
      <div className="p-4">
        <div className="flex gap-4">
          {/* Main Content - Left Panel */}
          <div className="flex-1 bg-[#FFFFFF] pb-4 border border-[#E5EAEF] rounded-[16px]">
            <React.Fragment>
              {/* Project Identity Section */}
              <div className="border-b border-gray-200 pb-6">
                <div className="px-5 pt-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                    <div>
                      <div className="text-sm text-[#212325] font-medium">Name of project</div>
                      <input
                        className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                        name="name"
                        value={project.name}
                        onChange={(e) => setProject({ ...project, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <div className="text-sm text-[#212325] font-medium">Business Unit</div>
                      <div className="mt-2">
                        <Select
                          options={[{ _id: "", name: "-" }, ...businessUnits.map((bu) => ({ _id: bu._id, name: `${bu.emoji || ""} ${bu.name}`.trim() }))]}
                          value={businessUnits.find((bu) => bu._id === project.business_unit_id) ? { _id: project.business_unit_id, name: businessUnits.find((bu) => bu._id === project.business_unit_id)?.name } : null}
                          onChange={(e) => {
                            const bu = businessUnits.find((b) => b._id === e._id);
                            setProject({ ...project, business_unit_id: e._id || "", business_unit_name: bu?.name || "" });
                          }}
                          placeholder="Business Unit"
                          width="w-full"
                          getValue={(v) => v._id}
                          getLabel={(v) => v.name}
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-sm text-[#212325] font-medium">Description</div>
                      <textarea
                        className="w-full border border-[#ced4da] rounded-[10px] text-sm font-normal p-2 mt-2 focus:outline-none focus:ring focus:ring-[#80bdff]"
                        rows="5"
                        placeholder="Project description..."
                        name="description"
                        value={project.description}
                        onChange={(e) => setProject({ ...project, description: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

            {/* Business Info Section */}
            <div className="py-6 px-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold mb-4">Business Info</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4">
                <div>
                  <div className="text-sm text-[#212325] font-medium">Industry</div>
                  <input
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="industry"
                    placeholder="e.g. Fintech, Healthcare, ESG..."
                    value={project.industry || ""}
                    onChange={(e) => setProject({ ...project, industry: e.target.value })}
                  />
                </div>
                <div>
                  <div className="text-sm text-[#212325] font-medium">Deal Size</div>
                  <select
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="deal_size"
                    value={project.deal_size || ""}
                    onChange={(e) => setProject({ ...project, deal_size: e.target.value })}>
                    <option value="">-</option>
                    <option value="< 50k">{"< 50k"}</option>
                    <option value="50k - 100k">50k - 100k</option>
                    <option value="100k - 500k">100k - 500k</option>
                    <option value="> 500k">{"> 500k"}</option>
                  </select>
                </div>
                <div>
                  <div className="text-sm text-[#212325] font-medium">Stage</div>
                  <select
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="stage"
                    value={project.stage || ""}
                    onChange={(e) => setProject({ ...project, stage: e.target.value })}>
                    <option value="">-</option>
                    <option value="run">Run</option>
                    <option value="prospection">Prospection</option>
                    <option value="dormant">Dormant</option>
                    <option value="dead">Dead</option>
                  </select>
                </div>
                <div>
                  <div className="text-sm text-[#212325] font-medium">Recovery</div>
                  <select
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="recovery_status"
                    value={project.recovery_status || ""}
                    onChange={(e) => setProject({ ...project, recovery_status: e.target.value })}>
                    <option value="">-</option>
                    <option value="active">Active</option>
                    <option value="ended">Ended</option>
                    <option value="sunk">Sunk</option>
                    <option value="litigation">Litigation</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <div className="text-sm text-[#212325] font-medium">Brief / Use Case</div>
                  <textarea
                    className="w-full border border-[#ced4da] rounded-[10px] text-sm font-normal p-2 mt-2 focus:outline-none focus:ring focus:ring-[#80bdff]"
                    rows="8"
                    placeholder="Describe the project use case, what was built, technologies used, value delivered..."
                    name="brief"
                    value={project.brief || ""}
                    onChange={(e) => setProject({ ...project, brief: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Administrative Section */}
            <div className="py-6 px-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold mb-4">Administrative Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4">
                <div>
                  <div className="text-sm text-[#212325] font-medium">VAT Number</div>
                  <input
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="tax"
                    value={project.clientVat}
                    onChange={(e) => setProject({ ...project, clientVat: e.target.value })}
                  />
                </div>

                <div>
                  <div className="text-sm text-[#212325] font-medium">SIREN</div>
                  <input
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="siren"
                    value={project.clientSiret}
                    onChange={(e) => setProject({ ...project, clientSiret: e.target.value })}
                  />
                </div>

                <div>
                  <div className="text-sm text-[#212325] font-medium">Client's Country</div>
                  <select
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="country"
                    value={project.country}
                    onChange={async (e) => {
                      const newCountry = e.target.value;
                      const taxType = await calculateTax(newCountry, project.organisation_id);
                      setProject((prevProject) => ({ ...prevProject, country: newCountry, taxType }));
                    }}>
                    <option value="">-</option>
                    <option value="France">France</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="Spain">Spain</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <div className="text-sm text-[#212325] font-medium flex items-center">
                    Tax Type
                    <div className="group ml-2 relative">
                      <a
                        href="https://accounting.selego.co/selecole/learn/6620fa1cc7df2607020d6655"
                        className="cursor-pointer text-gray-500 hover:text-gray-700"
                        target="_blank"
                        rel="noopener noreferrer">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </a>
                      <div className="absolute z-10 invisible group-hover:visible bg-gray-800 text-white text-xs rounded p-2 w-64 -ml-16 mt-2">
                        VAT is determined based on the location of the invoicing parties (Client's country and organization i.e Selego, Le collectif, etc.). For more information,
                        please click on i and check out the tutorial.
                      </div>
                    </div>
                  </div>
                  <input
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2 bg-gray-100 cursor-not-allowed"
                    name="taxType"
                    value={project.taxType || ""}
                    readOnly
                  />
                </div>

                <div>
                  <div className="text-sm text-[#212325] font-medium">Official Client Name (for billing)</div>
                  <input
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="clientName"
                    value={project.clientName}
                    onChange={(e) => setProject({ ...project, clientName: e.target.value })}
                  />
                </div>

                <div>
                  <div className="text-sm text-[#212325] font-medium">Email</div>
                  <input
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="clientEmail"
                    value={project.clientEmail}
                    onChange={(e) => setProject({ ...project, clientEmail: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="text-sm text-[#212325] font-medium">Address</div>
                  <input
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="address"
                    value={project.clientAddress}
                    onChange={(e) => setProject({ ...project, clientAddress: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Project Settings Section */}
            <div className="py-6 px-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold mb-4">Project Settings</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                <div>
                  <div className="text-sm text-[#212325] font-medium">Type</div>
                  <select
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="type"
                    value={project.type}
                    onChange={(e) => setProject({ ...project, type: e.target.value })}>
                    <option value="">-</option>
                    {["project", "admin", "timeoff"].map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-sm text-[#212325] font-medium">Status</div>
                  <select
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="status"
                    value={project.status}
                    onChange={(e) => {
                      if (e.target.value === "inactive" && budgets.length > 0) {
                        toast.error("Impossible de passer le projet en inactif car un budget actif existe !");
                        return;
                      }
                      setProject({ ...project, status: e.target.value });
                    }}>
                    <option value="">-</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <div className="text-sm text-[#212325] font-medium">Organisation (Primary - for invoicing)</div>
                  <div className="mt-2">
                    <Select
                      options={organisations}
                      value={organisations.find((o) => o._id === project.organisation_id)}
                      onChange={(e) => setProject({ ...project, organisation_id: e._id, organisation_name: e.name })}
                      placeholder="Organization"
                      width="w-full"
                      getValue={(v) => v._id}
                      getLabel={(v) => v.name}
                    />
                  </div>
                </div>
                <div>
                  <div className="text-sm text-[#212325] font-medium">Also visible in</div>
                  <div className="mt-2">
                    <MultiSelect
                      id="display-organisations"
                      options={organisations.filter((o) => o._id && o._id !== project.organisation_id).map((o) => ({ value: o._id, label: o.name }))}
                      onSelectedChange={(selected) => {
                        const selectedIds = selected.map((s) => s.value);
                        setProject({ ...project, display_organisation_ids: selectedIds });
                      }}
                      placeholder="Select additional organisations..."
                      values={project.display_organisation_ids?.map((id) => {
                        const org = organisations.find((o) => o._id === id);
                        return org ? { value: org._id, label: org.name } : null;
                      }).filter(Boolean) || []}
                    />
                  </div>
                </div>

              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                <div>
                  <div className="text-sm text-[#212325] font-medium mb-2">Market Type (for tax)</div>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <span className="text-sm font-normal text-gray-500">WBSO</span>
                      <input type="checkbox" name="wbso" checked={project.wbso_eligible} onChange={(e) => setProject({ ...project, wbso_eligible: e.target.checked })} />
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <span className="text-sm font-normal text-gray-500">CIR</span>
                      <input type="checkbox" name="cir" checked={project.cir_eligible} onChange={(e) => setProject({ ...project, cir_eligible: e.target.checked })} />
                    </label>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-4 mb-2">
                    <span className="text-sm text-[#212325] font-medium">Roadmap Tabs</span>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="disable_backnroad"
                        checked={project.disable_backnroad !== false}
                        onChange={(e) => setProject({ ...project, disable_backnroad: e.target.checked })}
                      />
                      <span className="text-sm font-normal text-gray-500">Deactivate Back'N'Road</span>
                    </label>
                  </div>
                  <div className="flex flex-row flex-wrap gap-2">
                    {["nnl", "tshirt", "planning", "budget", "by_module"].map((tab) => (
                      <label key={tab} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={project.roadmap_tabs?.includes(tab)}
                          onChange={(e) => {
                            const updatedTabs = e.target.checked ? [...(project.roadmap_tabs || []), tab] : (project.roadmap_tabs || []).filter((t) => t !== tab);
                            setProject({ ...project, roadmap_tabs: updatedTabs });
                          }}
                        />
                        <span className="text-sm font-normal text-gray-500">{tab}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Links Section */}
            <div className="py-6 px-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold mb-4">Links & Integrations</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                <div>
                  <div className="text-sm text-[#212325] font-medium">Website</div>
                  <input
                    className="projectsInput text-sm font-normal text-[#212325] rounded-[10px] mt-2"
                    name="website"
                    value={project.website}
                    onChange={(e) => setProject({ ...project, website: e.target.value })}
                  />
                </div>
                <div>
                  <div className="text-sm text-[#212325] font-medium">Notification Channel (Slack)</div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-gray-500">#</span>
                    <input
                      className="projectsInput text-sm font-normal text-[#212325] rounded-[10px]"
                      name="notification_channel"
                      value={project.notification_channel}
                      onChange={(e) => setProject({ ...project, notification_channel: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Comments Section */}
            <div className="py-6 px-5">
              <h2 className="text-lg font-semibold mb-4">Comments</h2>
              <Comments value={project.comments || []} onAdd={handleAddComment} onDelete={handleDeleteComment} />
            </div>

              {/* Save Button */}
              <div className="flex ml-3 mt-2">
                <button className="ml-[10px] bg-[#0560FD] text-[16px] font-medium text-[#fff] py-[12px] px-[22px] rounded-[10px]" onClick={onSave}>
                  Update
                </button>
              </div>
            </React.Fragment>
          </div>

          {/* Right Panel */}
          <div className="w-[280px] flex-shrink-0">
            <div className="sticky top-4 flex flex-col gap-4">
              <div className="bg-[#FFFFFF] p-5 border border-[#E5EAEF] rounded-[16px]">
                <h3 className="text-lg font-semibold mb-4">Project Logo</h3>
                <div className="flex flex-col items-center">
                  <FileInput value={project.logo} name="logo" folder="/project" onChange={(e) => setProject({ ...project, logo: e.target.value[0] })} />
                </div>
              </div>

              <div className="bg-[#FFFFFF] p-5 border border-[#E5EAEF] rounded-[16px]">
                <h3 className="text-lg font-semibold mb-4">Team</h3>
                <div className="flex flex-col gap-4">
                  <div>
                    <div className="text-sm text-[#212325] font-medium">Lead</div>
                    <SelectUser
                      value={{ _id: project.lead_id, name: project.lead_name, avatar: project.lead_avatar }}
                      userName={project.lead_name}
                      name="lead"
                      onChange={async (e) => {
                        try {
                          const { data } = await api.put(`/project/${project._id}`, {
                            lead_id: e?._id || null,
                            lead_name: e?.name || null,
                            lead_avatar: e?.avatar || null,
                            lead_email: e?.email || null,
                          });
                          setProject(data);
                          toast.success("Lead updated!");
                        } catch (err) {
                          console.error(err);
                          toast.error("Some Error!");
                        }
                      }}
                    />
                  </div>

                  <div>
                    <div className="text-sm text-[#212325] font-medium">Account Manager</div>
                    <SelectUser
                      value={{ _id: project.account_manager_id, name: project.account_manager_name, avatar: project.account_manager_avatar }}
                      userName={project.account_manager_name}
                      name="account_manager"
                      onChange={async (e) => {
                        try {
                          const { data } = await api.put(`/project/${project._id}`, {
                            account_manager_id: e?._id || null,
                            account_manager_name: e?.name || null,
                            account_manager_avatar: e?.avatar || null,
                            account_manager_email: e?.email || null,
                          });
                          setProject(data);
                          toast.success("Account Manager updated!");
                        } catch (err) {
                          console.error(err);
                          toast.error("Some Error!");
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

